import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { getBillingSubscriptionMetadata } from "../api/getBillingSubscriptionMetadata/getBillingSubscriptionMetadataApi";
import {
  BillingPaymentMethod,
  BillingSubscriptionMetadata,
} from "../api/getBillingSubscriptionMetadata/types";
import { getBillingSubscriptionInvoices } from "../api/getBillingSubscriptionInvoices/getBillingSubscriptionInvoicesApi";
import { BillingInvoice } from "../api/getBillingInvoices/types";
import { useBillingClientTable } from "./useBillingClientTable";
import { useBillingInvoiceDrawer } from "./useBillingInvoiceDrawer";
import { useBillingInvoicePdfDownload } from "./useBillingInvoicePdfDownload";
import { INVOICE_SORT_ACCESSORS } from "../config/billingInvoiceColumns";
import {
  formatBillingDate,
  formatBillingSubscriptionName,
} from "../utils/billingFormatters";

function describeNextInvoice(subscription: BillingSubscriptionMetadata) {
  if (!subscription.nextInvoiceDate) {
    return { value: "No upcoming invoice", detail: undefined };
  }

  const detail = subscription.frequency
    ? `Billed ${subscription.frequency}`
    : undefined;

  return { value: formatBillingDate(subscription.nextInvoiceDate), detail };
}

function describePaymentMethod(paymentMethod: BillingPaymentMethod | null) {
  if (!paymentMethod) {
    return { value: "No payment method on file", detail: undefined };
  }

  if (paymentMethod.type === "ach") {
    return { value: `ACH •••• ${paymentMethod.last4}`, detail: undefined };
  }

  const brand = paymentMethod.brand ?? "Card";
  const detail = paymentMethod.expiration
    ? `Expires ${paymentMethod.expiration}`
    : undefined;

  return { value: `${brand} •••• ${paymentMethod.last4}`, detail };
}

export function useBillingSubscriptionInvoicesPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [subscription, setSubscription] =
    useState<BillingSubscriptionMetadata | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const invoiceDrawer = useBillingInvoiceDrawer();
  const invoicePdfDownload = useBillingInvoicePdfDownload();

  const {
    sorting,
    handleSortingChange,
    pageIndex,
    pageSize,
    pageRows,
    handlePageChange,
  } = useBillingClientTable(invoices, INVOICE_SORT_ACCESSORS);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    Promise.all([
      getBillingSubscriptionMetadata(id, controller.signal),
      getBillingSubscriptionInvoices(id, controller.signal),
    ])
      .then(([metadataResponse, invoicesResponse]) => {
        setSubscription(metadataResponse.data);
        setInvoices(invoicesResponse.data);
        setLoading(false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) setLoading(false);
      });

    return () => controller.abort();
  }, [id]);

  const title = subscription
    ? formatBillingSubscriptionName(
        subscription.productName,
        subscription.trade,
      )
    : "Invoices";

  return {
    loading,
    title,
    nextInvoice: subscription && describeNextInvoice(subscription),
    paymentMethod:
      subscription && describePaymentMethod(subscription.paymentMethod),
    sorting,
    handleSortingChange,
    pageRows,
    totalCount: invoices.length,
    pageIndex,
    pageSize,
    handlePageChange,
    invoiceDrawer,
    invoicePdfDownload,
  };
}
